import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * useProductFilters : gestion des filtres synchronisés avec l'URL (search params)
 */
export const useProductFilters = filterFields => {
	const [searchParams, setSearchParams] = useSearchParams();

	// Transforme searchParams => { field: [val1, val2, ...] }
	const paramsToFilters = useCallback(params => {
		const filters = {};
		filterFields.forEach(field => {
			const value = params.get(field);
			filters[field] = value ? value.split(",") : [];
		});

		return filters;
	}, []);

	// Transforme { field: [val1, val2] } => URLSearchParams (en conservant les autres params !)
	const filtersToParams = useCallback(
		filters => {
			const params = new URLSearchParams(searchParams.toString());
			filterFields.forEach(field => {
				if (filters[field] && filters[field].length) {
					params.set(field, filters[field].join(","));
				} else {
					params.delete(field);
				}
			});
			return params;
		},
		[searchParams]
	);

	const filters = paramsToFilters(searchParams);

	const setFilters = useCallback(
		newFilters => {
			setSearchParams(filtersToParams(newFilters));
		},
		[setSearchParams, filtersToParams]
	);

	const setFilterValue = useCallback(
		(field, value) => {
			setFilters({ ...filters, [field]: [value] });
		},
		[filters, setFilters]
	);

	const removeFilterValue = useCallback(
		(field, valueToRemove) => {
			const current = filters[field] || [];
			const next = current.filter(v => v !== valueToRemove);
			const nextFilters = { ...filters, [field]: next };
			if (!next.length) {
				delete nextFilters[field];
			}
			setFilters(nextFilters);
		},
		[filters, setFilters]
	);

	const resetFilters = useCallback(() => {
		const params = new URLSearchParams(searchParams.toString());
		filterFields.forEach(field => params.delete(field));
		setSearchParams(params);
	}, [searchParams, setSearchParams]);

	const hasActiveFilters = Object.values(filters).some(arr => arr && arr.length);
	const selectedFiltersCount = Object.values(filters).reduce(
		(acc, arr) => acc + (arr?.length || 0),
		0
	);

	return {
		filters,
		setFilters,
		setFilterValue,
		removeFilterValue,
		resetFilters,
		hasActiveFilters,
		selectedFiltersCount,
	};
};
